/**
 * Búsqueda de estudiantes, detalle de saldos y registro de pago.
 *
 * Todo se lee de IndexedDB, así que funciona igual sin conexión. El registro del pago
 * confirma antes de guardar, evita el doble envío y muestra un resultado inequívoco.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DESCUENTO_ANUAL_INDIVIDUAL,
  DomainError,
  formatMoney,
  repartirDeuda,
  fromRow,
  money,
  newId,
  parsePositiveAmount,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  toDecimalString,
  type AllocationInput,
  type PaymentMethod,
} from "@senm/domain";
import { ApiRequestError, apiDownload, apiRequest } from "../api/client.js";
import type { DownloadJobDto, PaymentDto } from "@senm/contracts";
import {
  SELECCION_VACIA,
  StudentCalendar,
  type SeleccionCalendario,
} from "./Calendar.js";
import {
  db,
  normalizeForSearch,
  type LocalCharge,
  type LocalPayment,
  type LocalStudent,
} from "../local/db.js";
import { getInstallationId, type StoredSession } from "../local/session.js";
import {
  autoAllocate,
  openPdf,
  registerPayment,
  reprintReceipt,
} from "../services/payments.js";
import {
  cargosConMora,
  descuentoAnualDisponible,
  distribucionExacta,
  estadosDeMora,
  etiquetasDeMes,
} from "../services/cobroDesdeCalendario.js";

const METODOS: PaymentMethod[] = [
  "EFECTIVO",
  "TRANSFERENCIA",
  "DEPOSITO",
  "CHEQUE",
];

export function StudentsPage(props: {
  session: StoredSession;
  conexion: string;
  onCambio: () => Promise<void>;
  /** Avanza al terminar cada sincronización: obliga a releer los datos locales. */
  tickSync: number;
}): JSX.Element {
  const [texto, setTexto] = useState("");
  const [filtroSaldo, setFiltroSaldo] = useState<
    "TODOS" | "CON_SALDO" | "AL_DIA"
  >("TODOS");
  const [resultados, setResultados] = useState<LocalStudent[]>([]);
  const [seleccionado, setSeleccionado] = useState<LocalStudent | null>(null);

  const buscar = useCallback(async () => {
    const consulta = normalizeForSearch(texto);
    let coleccion = await db.students.toArray();

    if (consulta.length > 0) {
      coleccion = coleccion.filter(
        (estudiante) =>
          estudiante.searchName.includes(consulta) ||
          estudiante.code.toUpperCase().includes(consulta),
      );
    }
    if (filtroSaldo === "CON_SALDO")
      coleccion = coleccion.filter((item) => item.outstandingMinor > 0);
    if (filtroSaldo === "AL_DIA")
      coleccion = coleccion.filter((item) => item.outstandingMinor === 0);

    setResultados(coleccion.slice(0, 50));
  }, [texto, filtroSaldo]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  if (seleccionado !== null) {
    return (
      <StudentDetail
        session={props.session}
        student={seleccionado}
        conexion={props.conexion}
        onVolver={() => {
          setSeleccionado(null);
          void buscar();
        }}
        onCambio={props.onCambio}
        tickSync={props.tickSync}
      />
    );
  }

  return (
    <>
      <h1>Estudiantes</h1>
      <p className="ayuda">
        Búsqueda sobre los datos guardados en este dispositivo.
      </p>

      <div className="tarjeta">
        <label>
          <span>Buscar por nombre o código</span>
          <input
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
            placeholder="Ana María, EST-0007…"
            autoFocus
          />
        </label>
        <label>
          <span>Estado de pago</span>
          <select
            value={filtroSaldo}
            onChange={(evento) =>
              setFiltroSaldo(evento.target.value as typeof filtroSaldo)
            }
          >
            <option value="TODOS">Todos</option>
            <option value="CON_SALDO">Con saldo pendiente</option>
            <option value="AL_DIA">Al día</option>
          </select>
        </label>
      </div>

      {resultados.length === 0 ? (
        <p className="vacio">
          No se encontraron estudiantes con esos criterios.
        </p>
      ) : (
        <div className="tarjeta tabla-desplazable">
          <table>
            <caption className="ayuda">
              {resultados.length} resultado(s)
            </caption>
            <thead>
              <tr>
                <th scope="col">Código</th>
                <th scope="col">Estudiante</th>
                <th scope="col">Grado</th>
                <th scope="col" className="numero">
                  Saldo
                </th>
                <th scope="col">
                  <span className="saltar">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {resultados.map((estudiante) => (
                <tr key={estudiante.id}>
                  <td>{estudiante.code}</td>
                  <td>
                    {estudiante.fullName}
                    {!estudiante.active && (
                      <span className="marca-estado aviso"> ⚠ Inactivo</span>
                    )}
                  </td>
                  <td>
                    {estudiante.gradeName} {estudiante.sectionName}
                  </td>
                  <td className="numero">
                    {formatMoney(
                      fromRow(estudiante.outstandingMinor, estudiante.currency),
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="boton"
                      onClick={() => setSeleccionado(estudiante)}
                    >
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function StudentDetail(props: {
  session: StoredSession;
  student: LocalStudent;
  conexion: string;
  onVolver: () => void;
  onCambio: () => Promise<void>;
  tickSync: number;
}): JSX.Element {
  const [charges, setCharges] = useState<LocalCharge[]>([]);
  const [pagos, setPagos] = useState<LocalPayment[]>([]);
  const [student, setStudent] = useState(props.student);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [anulando, setAnulando] = useState<LocalPayment | null>(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  const [avisoAnulacion, setAvisoAnulacion] = useState<string | null>(null);
  const [errorAnulacion, setErrorAnulacion] = useState<string | null>(null);
  const [enviandoAnulacion, setEnviandoAnulacion] = useState(false);
  /**
   * Lo que el cajero marcó arriba, en el calendario. Vive aquí, en el contenedor,
   * porque tiene que cruzar desde el calendario hasta el formulario de cobro, que
   * son hermanos y no se ven entre sí.
   */
  const [seleccionMeses, setSeleccionMeses] =
    useState<SeleccionCalendario>(SELECCION_VACIA);
  /** Pago cuyo recibo se está generando, para no permitir dos clics seguidos. */
  const [descargandoRecibo, setDescargandoRecibo] = useState<string | null>(
    null,
  );
  const [errorRecibo, setErrorRecibo] = useState<string | null>(null);

  /**
   * Genera y descarga el PDF del recibo.
   *
   * Antes esto fallaba en SILENCIO: `reprintReceipt` devolvía `null` cuando el pago
   * no estaba en la base local —cualquier cobro hecho en otra caja—, y el botón
   * simplemente no hacía nada. Ahora se pide al servidor, que los tiene todos, y si
   * aun así falla se dice por qué en vez de dejar al cajero pulsando.
   */
  const descargarRecibo = async (paymentId: string): Promise<void> => {
    if (descargandoRecibo !== null) return;
    setDescargandoRecibo(paymentId);
    setErrorRecibo(null);
    try {
      const recibo = await reprintReceipt(paymentId, props.session);
      openPdf(recibo.receiptBytes, recibo.fileName);
    } catch (problema) {
      setErrorRecibo(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? "Ese recibo no está en este dispositivo y hace falta conexión para pedirlo al servidor."
          : "No fue posible generar el recibo.",
      );
    } finally {
      setDescargandoRecibo(null);
    }
  };

  /**
   * Descarga el recibo como PNG, para mandárselo al encargado por mensajería.
   *
   * Sale del MISMO PDF que se imprime, no de un segundo dibujo: maquetarlo aparte
   * acabaría con el papel y la imagen diciendo cosas distintas del mismo pago.
   *
   * Si el comprobante ocupa más de una hoja se avisa y no se genera. Mandar solo
   * la primera de tres sería peor que no mandar nada: el encargado creería tener
   * el comprobante completo.
   */
  const descargarReciboPng = async (paymentId: string): Promise<void> => {
    if (descargandoRecibo !== null) return;
    setDescargandoRecibo(paymentId);
    setErrorRecibo(null);
    try {
      const recibo = await reprintReceipt(paymentId, props.session);
      const { paginasDelRecibo, reciboComoPng } =
        await import("../services/reciboImagen.js");

      const paginas = await paginasDelRecibo(recibo.receiptBytes);
      if (paginas > 1) {
        setErrorRecibo(
          `Este comprobante ocupa ${paginas} hojas, así que no cabe en una imagen. ` +
            "Descárguelo como PDF.",
        );
        return;
      }

      const imagen = await reciboComoPng(recibo.receiptBytes);
      const url = URL.createObjectURL(imagen);
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = recibo.fileName.replace(/\.pdf$/i, ".png");
      enlace.click();
      // Se libera en el siguiente ciclo: revocarlo antes cancela la descarga.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (problema) {
      setErrorRecibo(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? "Convertir el recibo a imagen necesita conexión la primera vez."
          : "No fue posible generar la imagen del recibo.",
      );
    } finally {
      setDescargandoRecibo(null);
    }
  };

  const puedeAnular = props.session.user.permissions.includes("pagos:anular");

  /**
   * Anula un pago. NO lo borra: el servidor crea una operacion compensatoria y
   * ambas quedan visibles en el historial. Requiere conexion porque la
   * compensacion y el recalculo de saldo son decisiones del servidor.
   */
  const anularPago = async (): Promise<void> => {
    if (anulando === null || enviandoAnulacion) return;
    setEnviandoAnulacion(true);
    setErrorAnulacion(null);
    // El identificador de la anulación se genera una sola vez y se reutiliza como
    // clave idempotente: si la respuesta se pierde y el usuario reintenta, el
    // servidor devuelve la misma compensación en vez de crear una segunda.
    const voidId = newId();
    try {
      await apiRequest(`/v1/payments/${anulando.id}/void`, {
        method: "POST",
        idempotencyKey: `ANULACION:${voidId}`,
        body: {
          voidId,
          reason: motivoAnulacion,
          installationId: await getInstallationId(),
          at: new Date().toISOString(),
          operationalDate: new Date().toISOString().slice(0, 10),
        },
      });
      setAvisoAnulacion(
        "Pago anulado. El pago original se conserva y se registró una operación que lo revierte.",
      );
      setAnulando(null);
      setMotivoAnulacion("");
      await props.onCambio();
      await recargar();
    } catch (problema) {
      setErrorAnulacion(
        problema instanceof ApiRequestError
          ? problema.message
          : "No fue posible anular el pago.",
      );
    } finally {
      setEnviandoAnulacion(false);
    }
  };

  /**
   * Sube cada vez que un cobro o una exoneración pudo cambiar el calendario.
   *
   * Va aparte de `tickSync` porque un pago se registra ANTES de sincronizar: si el
   * calendario esperara a la sincronización, entre medias seguiría ofreciendo como
   * cobrables los meses recién pagados.
   */
  const [versionCalendario, setVersionCalendario] = useState(0);

  /**
   * Cargos y pagos del estudiante: los de ESTA computadora más los del servidor.
   *
   * El historial leía solo IndexedDB, y ahí están únicamente los cobros hechos en
   * este dispositivo. Un pago cobrado en otra caja no aparecía —y sin fila no hay
   * botón de recibo—, así que desde Administración no había forma de reimprimirlo
   * y parecía que el pago no existía. El servidor los tiene todos y el botón de
   * recibo ya sabe pedírselo.
   *
   * Los del servidor NO se guardan en IndexedDB: son de otra caja y mezclarlos con
   * los de aquí ensuciaría lo que este dispositivo tiene pendiente de enviar.
   * Sin conexión —o para una caja que solo puede ver sus propios pagos— se queda
   * con los locales, que es como estaba.
   */
  const recargar = useCallback(async () => {
    setCharges(
      await db.charges.where("studentId").equals(props.student.id).toArray(),
    );

    const locales = await db.payments
      .where("studentId")
      .equals(props.student.id)
      .toArray();
    let todos = locales;
    try {
      const remotos = await apiRequest<{ items: PaymentDto[] }>(
        `/v1/payments?studentId=${encodeURIComponent(props.student.id)}&limit=100`,
      );
      const deAqui = new Set(locales.map((pago) => pago.id));
      todos = [
        ...locales,
        ...remotos.items
          .filter((dto) => !deAqui.has(dto.id))
          .map(comoPagoLocal),
      ];
    } catch {
      // Sin conexión, o sin permiso para ver los de otros: quedan los de aquí.
    }
    setPagos(
      [...todos].sort((uno, otro) =>
        otro.capturedAt.localeCompare(uno.capturedAt),
      ),
    );

    const actualizado = await db.students.get(props.student.id);
    if (actualizado !== undefined) setStudent(actualizado);
  }, [props.student.id]);

  // `tickSync` va en las dependencias: al terminar una sincronización el pago pasa de
  // «pendiente» a «confirmado» con su número de recibo sin cambiar de pantalla.
  useEffect(() => {
    void recargar();
  }, [recargar, props.tickSync]);

  const pendientes = charges.filter(
    (charge) => !charge.voided && charge.amountMinor - charge.appliedMinor > 0,
  );

  /**
   * Lo que se debe HOY, separado de lo que aún no vence.
   *
   * La tarjeta sumaba todos los cargos vivos, incluidos los del ciclo siguiente que
   * el sistema genera para poder cobrarlos por anticipado. Un estudiante al
   * corriente aparecía debiendo Q1,465 cuando lo que se le podía reclamar eran
   * Q825: el resto eran dos mensualidades del año próximo que nadie le ha pedido.
   *
   * Se calcula sobre los cargos del dispositivo, los MISMOS que se listan abajo en
   * «Conceptos pendientes», para que la cifra de arriba y la lista de abajo no
   * puedan contarse distinto.
   */
  const reparto = repartirDeuda(
    pendientes.map((charge) => ({
      dueDate: charge.dueDate,
      pendienteMinor: charge.amountMinor - charge.appliedMinor,
    })),
    new Date().toISOString().slice(0, 10),
  );

  /**
   * De qué se compone el saldo, en una línea.
   *
   * La tarjeta daba una cifra sin explicación. Ante «Q825» no se podía saber que
   * Q300 eran de agosto y Q525 venían arrastrados desde enero y febrero; había que
   * bajar hasta «Conceptos pendientes» y sumar a mano. En una ventanilla, con el
   * encargado preguntando de qué es ese monto, eso no sirve.
   */
  const desgloseSaldo = [
    reparto.vencidoMinor > 0
      ? `${formatMoney(fromRow(reparto.vencidoMinor, student.currency))} vencido`
      : null,
    reparto.delMesMinor > 0
      ? `${formatMoney(fromRow(reparto.delMesMinor, student.currency))} vence este mes`
      : null,
  ]
    .filter((parte): parte is string => parte !== null)
    .join(" · ");

  return (
    <>
      <button type="button" className="boton" onClick={props.onVolver}>
        ← Volver a la búsqueda
      </button>

      <h1>{student.fullName}</h1>
      <p className="ayuda">
        {student.code} · {student.gradeName} {student.sectionName}
        {!student.active && " · Estudiante inactivo"}
      </p>

      <div className="rejilla">
        <div className="tarjeta dato">
          <span className="etiqueta">Saldo pendiente</span>
          <span className="valor">
            {formatMoney(fromRow(reparto.exigibleMinor, student.currency))}
          </span>
          {desgloseSaldo !== "" && <p className="ayuda">{desgloseSaldo}</p>}
        </div>
        {reparto.porVenirMinor > 0 && (
          <div className="tarjeta dato">
            <span className="etiqueta">Por venir</span>
            <span className="valor">
              {formatMoney(fromRow(reparto.porVenirMinor, student.currency))}
            </span>
            <p className="ayuda">
              Aún no vence. Se puede pagar por anticipado, pero no se debe
              todavía.
            </p>
          </div>
        )}
        {student.creditMinor > 0 && (
          <div className="tarjeta dato">
            <span className="etiqueta">Saldo a favor</span>
            <span className="valor">
              {formatMoney(fromRow(student.creditMinor, student.currency))}
            </span>
          </div>
        )}
      </div>

      {/* El calendario se calcula en el servidor porque la mora depende de la fecha
          operativa y de los abonos ya aplicados. Sin conexión no se muestra: en su
          lugar quedan los conceptos pendientes que el dispositivo ya tenía. */}
      {props.conexion === "EN_LINEA" && student.cycleId !== "" && (
        <StudentCalendar
          session={props.session}
          studentId={student.id}
          cycleId={student.cycleId}
          conexion={props.conexion}
          onSeleccionPago={setSeleccionMeses}
          onCambio={props.onCambio}
          recarga={versionCalendario + props.tickSync}
        />
      )}

      {props.session.user.permissions.includes("descargas:generar") && (
        <BotonDescarga studentId={student.id} conexion={props.conexion} />
      )}

      <h2>Conceptos pendientes</h2>
      {pendientes.length === 0 ? (
        <p className="vacio">El estudiante no tiene conceptos pendientes.</p>
      ) : (
        <div className="tarjeta tabla-desplazable">
          <table>
            <thead>
              <tr>
                <th scope="col">Concepto</th>
                <th scope="col">Vence</th>
                <th scope="col" className="numero">
                  Importe
                </th>
                <th scope="col" className="numero">
                  Pendiente
                </th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((charge) => (
                <tr key={charge.id}>
                  <td>{charge.conceptName}</td>
                  <td>{charge.dueDate ?? "—"}</td>
                  <td className="numero">
                    {formatMoney(fromRow(charge.amountMinor, charge.currency))}
                  </td>
                  <td className="numero">
                    {formatMoney(
                      fromRow(
                        charge.amountMinor - charge.appliedMinor,
                        charge.currency,
                      ),
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {avisoAnulacion !== null && (
        <div className="aviso-caja ok" role="status">
          {avisoAnulacion}
        </div>
      )}

      {anulando !== null && (
        <div className="tarjeta" role="dialog" aria-labelledby="titulo-anular">
          <h2 id="titulo-anular">Anular pago</h2>
          <p>
            Va a anular el pago de{" "}
            <strong>
              {formatMoney(fromRow(anulando.amountMinor, anulando.currency))}
            </strong>
            {anulando.receiptNumber !== null &&
              ` (recibo ${anulando.receiptNumber})`}
            .
          </p>
          <div className="aviso-caja" role="alert">
            <strong>El pago no se borra.</strong> Se registra una operación que
            lo revierte, y las dos quedan visibles en el historial. El saldo del
            estudiante vuelve a su valor anterior y la acción queda en la
            auditoría.
          </div>

          {errorAnulacion !== null && (
            <div className="aviso-caja error" role="alert">
              {errorAnulacion}
            </div>
          )}

          <label>
            Motivo de la anulación
            <input
              type="text"
              value={motivoAnulacion}
              onChange={(evento) => setMotivoAnulacion(evento.target.value)}
              minLength={5}
              maxLength={500}
              required
              placeholder="Cobro duplicado por error de captura"
            />
          </label>

          <div className="acciones">
            <button
              type="button"
              className="boton primario"
              onClick={() => void anularPago()}
              disabled={enviandoAnulacion || motivoAnulacion.trim().length < 5}
            >
              {enviandoAnulacion ? "Anulando…" : "Sí, anular el pago"}
            </button>
            <button type="button" onClick={() => setAnulando(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {!mostrarFormulario && pendientes.length > 0 && student.active && (
        <button
          type="button"
          className="boton primario"
          onClick={() => setMostrarFormulario(true)}
        >
          {seleccionMeses.elegidos.length > 0 ||
          seleccionMeses.otrosElegidos.length > 0
            ? `Cobrar ${[
                seleccionMeses.elegidos.length > 0
                  ? `${seleccionMeses.elegidos.length} mes(es)`
                  : null,
                seleccionMeses.otrosElegidos.length > 0
                  ? `${seleccionMeses.otrosElegidos.length} concepto(s)`
                  : null,
              ]
                .filter((parte): parte is string => parte !== null)
                .join(" y ")} — ${formatMoney(
                money(seleccionMeses.totalMinor, seleccionMeses.currency),
              )}`
            : "Registrar pago"}
        </button>
      )}

      {mostrarFormulario && (
        <PaymentForm
          session={props.session}
          student={student}
          charges={charges}
          seleccion={seleccionMeses}
          onCancelar={() => setMostrarFormulario(false)}
          onRegistrado={async () => {
            setMostrarFormulario(false);
            setVersionCalendario((version) => version + 1);
            await recargar();
            await props.onCambio();
          }}
        />
      )}

      <h2>Historial de pagos</h2>

      {errorRecibo !== null && (
        <div className="aviso-caja error" role="alert">
          {errorRecibo}
        </div>
      )}
      {pagos.length === 0 ? (
        <p className="vacio">Este estudiante no tiene pagos registrados.</p>
      ) : (
        <div className="tarjeta tabla-desplazable">
          <table>
            {/* Dejó de ser «en este dispositivo» el día que la tabla empezó a
                traer también los cobros hechos en otra caja. */}
            <caption className="oculto-visual">
              Pagos registrados del estudiante
            </caption>
            <thead>
              <tr>
                <th scope="col">Fecha</th>
                <th scope="col">Método</th>
                <th scope="col">Recibo</th>
                <th scope="col">Estado</th>
                <th scope="col" className="numero">
                  Importe
                </th>
                <th scope="col">
                  <span className="saltar">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagos.map((pago) => (
                <tr key={pago.id}>
                  <td>{pago.operationalDate}</td>
                  <td>
                    {PAYMENT_METHOD_LABELS[pago.method as PaymentMethod] ??
                      pago.method}
                  </td>
                  <td>{pago.receiptNumber ?? "Provisional"}</td>
                  <td>
                    <EstadoPago pago={pago} />
                  </td>
                  <td className="numero">
                    {formatMoney(fromRow(pago.amountMinor, pago.currency))}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="boton compacto"
                      disabled={descargandoRecibo === pago.id}
                      onClick={() => void descargarRecibo(pago.id)}
                    >
                      {descargandoRecibo === pago.id
                        ? "Generando…"
                        : "↓ Recibo PDF"}
                    </button>
                    <button
                      type="button"
                      className="boton compacto"
                      disabled={descargandoRecibo === pago.id}
                      onClick={() => void descargarReciboPng(pago.id)}
                    >
                      {descargandoRecibo === pago.id
                        ? "Generando…"
                        : "↓ Recibo PNG"}
                    </button>
                    {/* Solo Administración, solo pagos confirmados y solo en línea:
                        anular crea una compensación que el servidor debe calcular. */}
                    {puedeAnular &&
                      pago.status === "CONFIRMADO" &&
                      props.conexion === "EN_LINEA" && (
                        <button
                          type="button"
                          onClick={() => {
                            setAnulando(pago);
                            setMotivoAnulacion("");
                            setErrorAnulacion(null);
                          }}
                        >
                          Anular
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Traduce el fallo de un cobro a algo que se pueda hacer.
 *
 * `CARGO_NO_ENCONTRADO` decía «El concepto ya no está disponible», y en la
 * ventanilla eso suena a que el cobro se anuló o dejó de existir. Casi siempre
 * es otra cosa: el calendario lo pinta el SERVIDOR y la validación del cobro
 * mira los cargos de ESTA computadora, así que un cargo recién generado desde
 * Administración se ve en pantalla y todavía no está aquí abajo.
 *
 * Pasa justo el día que se monta el colegio, que es cuando menos ayuda un
 * mensaje que no dice qué hacer.
 */
function mensajeDeCobro(problema: unknown): string {
  if (!(problema instanceof DomainError)) {
    return "No fue posible registrar el pago. Verifique los datos.";
  }
  if (problema.code === "CARGO_NO_ENCONTRADO") {
    return (
      "Este cobro todavía no está en esta computadora. " +
      "Pulse Sincronizar y vuelva a intentarlo."
    );
  }
  return problema.message;
}

/**
 * Un pago del servidor con la forma que usa la tabla del historial.
 *
 * Solo para enseñarlo: nunca se escribe en IndexedDB. El recibo de estos NO se
 * arma con esto —`reprintReceipt` ve que el pago no está en este dispositivo y se
 * lo pide al servidor, que lo emite con su consecutivo oficial—, así que aquí
 * basta con lo que la tabla pinta.
 */
function comoPagoLocal(dto: PaymentDto): LocalPayment {
  return {
    id: dto.id,
    studentId: dto.studentId,
    studentCode: dto.studentCode,
    studentName: dto.studentName,
    cycleId: dto.cycleId,
    cycleName: dto.allocations[0]?.cycleName ?? undefined,
    gradeName: dto.gradeName,
    sectionName: dto.sectionName,
    amountMinor: dto.amount.minor,
    currency: dto.amount.currency,
    method: dto.method,
    reference: dto.reference,
    allocations: dto.allocations.map((linea) => ({
      chargeId: linea.chargeId,
      conceptId: linea.conceptId,
      conceptName: linea.conceptName,
      amountMinor: linea.amount.minor,
      balanceBeforeMinor: linea.balanceBefore.minor,
      balanceAfterMinor: linea.balanceAfter.minor,
    })),
    creditMinor: dto.creditAmount.minor,
    status: dto.status,
    effectiveAt: dto.effectiveAt,
    capturedAt: dto.capturedAt,
    operationalDate: dto.operationalDate,
    userId: dto.userId,
    userName: dto.userName,
    installationId: dto.installationId,
    note: dto.note,
    balanceBeforeMinor: dto.studentBalanceBefore.minor,
    balanceAfterMinor: dto.studentBalanceAfter.minor,
    receiptNumber: dto.receiptNumber,
    statusMessage: null,
  };
}

function EstadoPago({ pago }: { pago: LocalPayment }): JSX.Element {
  const etiqueta =
    PAYMENT_STATUS_LABELS[pago.status as keyof typeof PAYMENT_STATUS_LABELS] ??
    pago.status;
  const tono =
    pago.status === "CONFIRMADO"
      ? "ok"
      : pago.status === "PENDIENTE"
        ? "aviso"
        : "error";
  const simbolo =
    pago.status === "CONFIRMADO"
      ? "✓"
      : pago.status === "PENDIENTE"
        ? "◷"
        : "!";
  return (
    <>
      <span className={`marca-estado ${tono}`}>
        {simbolo} {etiqueta}
      </span>
      {pago.statusMessage !== null && (
        <p className="ayuda">{pago.statusMessage}</p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function PaymentForm(props: {
  session: StoredSession;
  student: LocalStudent;
  charges: LocalCharge[];
  /** Meses marcados arriba en el calendario. Puede venir vacía: cobrar un importe
   *  suelto sigue siendo válido, y entonces se reparte por antigüedad. */
  seleccion: SeleccionCalendario;
  onCancelar: () => void;
  onRegistrado: () => Promise<void>;
}): JSX.Element {
  /*
   * Descuento por pagar el año completo, si esta selección da derecho a él.
   *
   * Se calcula antes que nada porque decide hasta el importe que aparece
   * escrito al abrir el formulario: el cajero no tiene que restar nada a mano.
   */
  const descuentoPosible = useMemo(
    () =>
      descuentoAnualDisponible(props.seleccion, DESCUENTO_ANUAL_INDIVIDUAL),
    [props.seleccion],
  );
  // Viene marcado: el colegio concede la rebaja SIEMPRE que se pague el año
  // completo, así que dejarla apagada por defecto solo serviría para olvidarla.
  const [aplicarDescuento, setAplicarDescuento] = useState(true);
  const descuento =
    descuentoPosible !== null && aplicarDescuento ? descuentoPosible : null;
  const totalACobrar =
    props.seleccion.totalMinor - (descuento?.totalMinor ?? 0);

  const [importe, setImporte] = useState(() =>
    props.seleccion.totalMinor > 0
      ? toDecimalString(
          money(
            props.seleccion.totalMinor -
              (descuentoPosible?.totalMinor ?? 0),
            props.seleccion.currency,
          ),
        )
      : "",
  );
  const [metodo, setMetodo] = useState<PaymentMethod>("EFECTIVO");
  /**
   * Fecha del cobro, modificable antes de emitir.
   *
   * Hace falta para registrar un pago que se recibió otro día —un depósito del
   * viernes que se captura el lunes— y para que el recibo lleve la fecha real.
   * Cambia también la fecha con la que se calcula la mora, así que se avisa.
   */
  const [fechaPago, setFechaPago] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  /**
   * Fecha con la que se EXTIENDE el comprobante, distinta de la del pago.
   *
   * El recibo del colegio lleva las dos. Suelen coincidir, pero un depósito del
   * viernes puede entregarse el lunes, y el papel debe decir ambas cosas.
   */
  const [fechaEmision, setFechaEmision] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [referencia, setReferencia] = useState("");
  const [nota, setNota] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const enviado = useRef(false);

  /**
   * Cargos locales enriquecidos con el estado de mora que trajo el calendario.
   *
   * El dispositivo no guarda mora —depende de la fecha operativa y por eso se
   * calcula en el servidor—, así que sin esto la validación local ve solo el
   * capital y rechaza un cobro legítimo: marcar un mes con Q996 de mora fallaba
   * con «supera su saldo pendiente (Q400.00)», ignorando la mora que la pantalla
   * de arriba acababa de mostrar. Con el estado puesto, cliente y servidor
   * ejecutan exactamente la misma comprobación.
   */
  const domainCharges = useMemo(
    () => cargosConMora(props.charges, props.seleccion),
    [props.charges, props.seleccion],
  );

  /** `true` si el importe sigue siendo exactamente el de lo marcado arriba. */
  const importeCoincideConSeleccion = useMemo(() => {
    /*
     * Cuenta lo marcado en LAS DOS tablas.
     *
     * Mirando solo los meses, un cobro de puros conceptos —la inscripción sola,
     * sin mensualidad— caía en el reparto por antigüedad y el dinero terminaba
     * en el cargo más viejo. El cajero marcaba «Inscripción», el recibo salía
     * con otro concepto y la inscripción seguía debiéndose.
     */
    if (
      props.seleccion.elegidos.length === 0 &&
      props.seleccion.otrosElegidos.length === 0
    ) {
      return false;
    }
    try {
      return (
        parsePositiveAmount(importe, props.student.currency).minor ===
        totalACobrar
      );
    } catch {
      return false;
    }
  }, [importe, props.seleccion, props.student.currency, totalACobrar]);

  const distribucion: AllocationInput[] = useMemo(() => {
    // Si el cajero marcó meses arriba y no tocó el importe, se cobra EXACTAMENTE
    // eso. Repartir por antigüedad en ese caso podría aplicar el dinero a un mes
    // distinto del que se le dijo al cliente que estaba pagando.
    if (importeCoincideConSeleccion) {
      // Meses y conceptos sueltos van juntos a la distribución: el cajero marcó
      // ambas tablas y el importe cuadra con la suma de las dos.
      return distribucionExacta(props.seleccion, descuento);
    }
    try {
      const monto = parsePositiveAmount(importe, props.student.currency);
      return autoAllocate(monto, domainCharges);
    } catch {
      return [];
    }
  }, [
    importe,
    domainCharges,
    props.student.currency,
    props.seleccion,
    importeCoincideConSeleccion,
    descuento,
  ]);

  const totalDistribuido = distribucion.reduce(
    (total, linea) => total + linea.amount.minor,
    0,
  );
  const importeMinor = (() => {
    try {
      return parsePositiveAmount(importe, props.student.currency).minor;
    } catch {
      return 0;
    }
  })();
  const excedente = Math.max(0, importeMinor - totalDistribuido);

  const registrar = async (): Promise<void> => {
    // Barrera contra el doble clic y contra el reenvío por reintento de red.
    if (enviado.current || enviando) return;
    enviado.current = true;
    setEnviando(true);
    setError(null);

    try {
      const resultado = await registerPayment({
        session: props.session,
        student: props.student,
        charges: props.charges,
        monthlyStates: estadosDeMora(props.seleccion),
        amountMinor: importeMinor,
        method: metodo,
        reference: referencia.trim() === "" ? null : referencia.trim(),
        allocations: distribucion,
        note: nota.trim() === "" ? null : nota.trim(),
        allowOverpayment: true,
        // El pago se registra SIEMPRE con el instante real: es lo que fija la mora
        // y la fecha operativa. Las fechas que el cajero elige son de presentación.
        now: new Date().toISOString(),
        paidOn: fechaPago,
        issuedAt: fechaEmision,
        cycleName: props.seleccion.cycleName,
        monthLabels: etiquetasDeMes(props.seleccion),
        annualDiscount: descuento,
      });

      openPdf(resultado.receiptBytes, resultado.fileName);
      await props.onRegistrado();
    } catch (problema) {
      enviado.current = false;
      setError(mensajeDeCobro(problema));
      setConfirmando(false);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="tarjeta" aria-label="Registrar pago">
      <h2>Registrar pago</h2>

      {/* Los meses marcados arriba se repiten AQUÍ, junto al importe. El cajero
          tiene al cliente enfrente y no puede pedirle que suba a mirar el
          calendario para saber qué está pagando. */}
      {(props.seleccion.elegidos.length > 0 ||
        props.seleccion.otrosElegidos.length > 0) && (
        <div className="resumen-meses">
          <h3>
            Se van a cobrar {props.seleccion.elegidos.length} mes(es)
            {props.seleccion.otrosElegidos.length > 0 &&
              ` y ${props.seleccion.otrosElegidos.length} concepto(s)`}
            {props.seleccion.cycleName !== "" &&
              ` · ${props.seleccion.cycleName}`}
          </h3>

          <table className="tabla-meses">
            <caption className="oculto-visual">
              Meses seleccionados con su capital, mora y total
            </caption>
            <thead>
              <tr>
                <th scope="col">Mes</th>
                <th scope="col">Estado</th>
                <th scope="col" className="numero">
                  Capital
                </th>
                <th scope="col" className="numero">
                  Mora
                </th>
                <th scope="col" className="numero">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {props.seleccion.elegidos.map((mes) => (
                <tr key={mes.chargeId}>
                  <th scope="row">{mes.monthLabel}</th>
                  <td>
                    <span className="marca-estado">
                      {mes.statusSymbol} {mes.statusLabel}
                    </span>
                  </td>
                  <td className="numero">
                    {formatMoney(
                      money(mes.capitalMinor, props.seleccion.currency),
                    )}
                  </td>
                  <td className="numero">
                    {mes.lateFeeMinor > 0 ? (
                      <>
                        {formatMoney(
                          money(mes.lateFeeMinor, props.seleccion.currency),
                        )}
                        <span className="dias-mora">
                          {" "}
                          · {mes.lateFeeDays} días
                        </span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="numero fuerte">
                    {formatMoney(
                      money(mes.totalMinor, props.seleccion.currency),
                    )}
                  </td>
                </tr>
              ))}
              {/*
                Los conceptos sin mes se listan aquí mismo, debajo de los meses.
                En el resumen sí van juntos: lo que el cajero necesita ver antes de
                cobrar es TODO lo que va en este pago y cuánto suma. La separación
                en dos tablas está arriba, en el calendario, que es donde importa
                distinguir qué sigue el orden obligatorio y qué no.
              */}
              {props.seleccion.otrosElegidos.map((cargo) => (
                <tr key={cargo.chargeId}>
                  <th scope="row">{cargo.conceptName}</th>
                  <td>
                    <span className="marca-estado neutro">
                      Concepto · vence {cargo.dueDate ?? "sin fecha"}
                    </span>
                  </td>
                  <td className="numero">
                    {formatMoney(
                      money(cargo.totalMinor, props.seleccion.currency),
                    )}
                  </td>
                  <td className="numero">—</td>
                  <td className="numero">
                    {formatMoney(
                      money(cargo.totalMinor, props.seleccion.currency),
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={4}>
                  {descuento === null ? "Total a cobrar" : "Suma de los meses"}
                </th>
                <td className="numero fuerte">
                  {formatMoney(
                    money(props.seleccion.totalMinor, props.seleccion.currency),
                  )}
                </td>
              </tr>
              {descuento !== null && (
                <>
                  <tr>
                    <th scope="row" colSpan={4}>
                      Descuento {descuento.porcentaje}% por pago anual
                    </th>
                    <td className="numero">
                      −
                      {formatMoney(
                        money(descuento.totalMinor, props.seleccion.currency),
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" colSpan={4}>
                      Total a cobrar
                    </th>
                    <td className="numero fuerte">
                      {formatMoney(
                        money(totalACobrar, props.seleccion.currency),
                      )}
                    </td>
                  </tr>
                </>
              )}
            </tfoot>
          </table>

          {/* Lo que queda después de este cobro, dicho antes de cobrarlo. */}
          <div className="tras-el-cobro">
            {props.seleccion.mesesPagados.length > 0 && (
              <p className="ok">
                <strong>Ya pagados:</strong>{" "}
                {props.seleccion.mesesPagados.join(", ")}
              </p>
            )}
            {props.seleccion.mesesPendientes.length > 0 ? (
              <p className="aviso">
                <strong>Seguirá debiendo:</strong>{" "}
                {props.seleccion.mesesPendientes.join(", ")}
              </p>
            ) : (
              <p className="ok">
                <strong>Con este pago queda al día</strong> en{" "}
                {props.seleccion.cycleName}.
              </p>
            )}
          </div>

          {descuentoPosible !== null && (
            <div className="tras-el-cobro">
              <label className="casilla">
                <input
                  type="checkbox"
                  checked={aplicarDescuento}
                  onChange={(evento) => {
                    const activo = evento.target.checked;
                    setAplicarDescuento(activo);
                    // El importe se reescribe con la casilla: dejar el viejo
                    // mandaria el cobro al reparto por antiguedad sin avisar.
                    setImporte(
                      toDecimalString(
                        money(
                          props.seleccion.totalMinor -
                            (activo ? descuentoPosible.totalMinor : 0),
                          props.seleccion.currency,
                        ),
                      ),
                    );
                  }}
                />
                <span>
                  Aplicar el descuento del {descuentoPosible.porcentaje}% por
                  pagar el año completo (
                  {formatMoney(
                    money(
                      descuentoPosible.totalMinor,
                      props.seleccion.currency,
                    ),
                  )}
                  )
                </span>
              </label>
              <p className="ayuda">
                Se está cobrando la colegiatura completa de{" "}
                {props.seleccion.cycleName}, así que corresponde la rebaja. Sale
                en el recibo como su propia línea.
              </p>
            </div>
          )}

          {!importeCoincideConSeleccion && (
            <p className="aviso-caja" role="status">
              El importe ya no coincide con los meses marcados, así que se
              repartirá empezando por la deuda más antigua. Para cobrar
              exactamente lo marcado, escriba{" "}
              <strong>
                {toDecimalString(
                  money(totalACobrar, props.seleccion.currency),
                )}
              </strong>
              .
            </p>
          )}
        </div>
      )}

      <label>
        <span>Importe a cobrar ({props.student.currency})</span>
        <input
          value={importe}
          onChange={(evento) => setImporte(evento.target.value)}
          inputMode="decimal"
          placeholder="300.00"
          autoFocus
          aria-invalid={error !== null}
        />
      </label>

      <label>
        <span>Fecha de emisión del recibo</span>
        <input
          type="date"
          value={fechaEmision}
          onChange={(evento) => setFechaEmision(evento.target.value)}
        />
      </label>

      <label>
        <span>Fecha del cobro</span>
        <input
          type="date"
          value={fechaPago}
          onChange={(evento) => setFechaPago(evento.target.value)}
          max={new Date().toISOString().slice(0, 10)}
        />
      </label>
      {fechaPago !== new Date().toISOString().slice(0, 10) && (
        <p className="ayuda">
          El recibo se imprimirá con fecha <strong>{fechaPago}</strong>. Es solo
          lo que se lee en el papel: el pago queda registrado hoy y la mora es
          la de hoy.
        </p>
      )}

      <label>
        <span>Método de pago</span>
        <select
          value={metodo}
          onChange={(evento) => setMetodo(evento.target.value as PaymentMethod)}
        >
          {METODOS.map((item) => (
            <option key={item} value={item}>
              {PAYMENT_METHOD_LABELS[item]}
            </option>
          ))}
        </select>
      </label>

      {metodo !== "EFECTIVO" && (
        <label>
          <span>Referencia (boleta, cheque)</span>
          <input
            value={referencia}
            onChange={(evento) => setReferencia(evento.target.value)}
            placeholder="BOL-4512"
            maxLength={40}
          />
        </label>
      )}

      <label>
        <span>Observación (opcional)</span>
        <textarea
          value={nota}
          onChange={(evento) => setNota(evento.target.value)}
          rows={2}
          maxLength={500}
        />
      </label>

      {distribucion.length > 0 && (
        <>
          <h2>Se aplicará así</h2>
          <div className="tabla-desplazable">
            <table>
              <thead>
                <tr>
                  <th scope="col">Concepto</th>
                  <th scope="col" className="numero">
                    Se aplica
                  </th>
                </tr>
              </thead>
              <tbody>
                {distribucion.map((linea) => {
                  const charge = props.charges.find(
                    (item) => item.id === linea.chargeId,
                  );
                  return (
                    <tr key={linea.chargeId}>
                      <td>{charge?.conceptName ?? linea.chargeId}</td>
                      <td className="numero">{formatMoney(linea.amount)}</td>
                    </tr>
                  );
                })}
                {excedente > 0 && (
                  <tr>
                    <td>Saldo a favor del estudiante</td>
                    <td className="numero">
                      {formatMoney(fromRow(excedente, props.student.currency))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}

      <div className="pila">
        <button
          type="button"
          className="boton primario"
          disabled={importeMinor <= 0 || distribucion.length === 0 || enviando}
          onClick={() => setConfirmando(true)}
        >
          Continuar
        </button>
        <button
          type="button"
          className="boton"
          onClick={props.onCancelar}
          disabled={enviando}
        >
          Cancelar
        </button>
      </div>

      {/* Confirmación explícita antes de una acción monetaria. */}
      {confirmando && (
        <div
          className="aviso-caja"
          role="alertdialog"
          aria-label="Confirmar pago"
        >
          <p>
            <strong>
              ¿Confirma el cobro de{" "}
              {formatMoney(fromRow(importeMinor, props.student.currency))} a{" "}
              {props.student.fullName}?
            </strong>
          </p>
          <p className="ayuda">
            Se emitirá un recibo provisional. Quedará marcado como pendiente
            hasta que el servidor lo confirme.
          </p>
          <div className="pila">
            <button
              type="button"
              className="boton primario"
              onClick={() => void registrar()}
              disabled={enviando}
            >
              {enviando ? "Registrando…" : "Sí, registrar el pago"}
            </button>
            <button
              type="button"
              className="boton"
              onClick={() => setConfirmando(false)}
              disabled={enviando}
            >
              Revisar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Botón «Descargar» del estudiante.
 *
 * Reglas del contrato que la interfaz debe respetar (§14):
 *  · Regla 17: mientras haya una generación activa para ESE estudiante, el botón se
 *    deshabilita. Es la primera barrera contra pedir dos veces lo mismo.
 *  · Regla 2: si el usuario sale de la pantalla, la generación sigue en segundo
 *    plano; al volver se refleja su estado.
 *  · Regla 3: el botón se habilita al terminar, con éxito o con error.
 *  · Regla 5: un fallo ocurrido mientras el usuario estaba fuera se informa al volver.
 */
function BotonDescarga(props: {
  studentId: string;
  conexion: string;
}): JSX.Element | null {
  const [trabajos, setTrabajos] = useState<DownloadJobDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [solicitando, setSolicitando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const datos = await apiRequest<{ items: DownloadJobDto[] }>(
        "/v1/downloads",
      );
      setTrabajos(
        datos.items.filter((trabajo) => trabajo.studentId === props.studentId),
      );
    } catch {
      // El historial es informativo: sin él, el botón sigue funcionando.
    }
  }, [props.studentId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Un fallo que ocurrió mientras el usuario no estaba se informa al volver.
  const fallido = trabajos.find((trabajo) => trabajo.status === "FALLIDO");
  const activo = trabajos.some(
    (trabajo) => trabajo.status === "EN_COLA" || trabajo.status === "GENERANDO",
  );
  const listo = trabajos.find(
    (trabajo) => trabajo.status === "LISTO" && trabajo.available,
  );

  if (props.conexion !== "EN_LINEA") return null;

  const solicitar = async (): Promise<void> => {
    if (solicitando || activo) return;
    setSolicitando(true);
    setError(null);
    try {
      await apiRequest("/v1/downloads", {
        method: "POST",
        body: {
          jobId: newId(),
          studentId: props.studentId,
          // El instante en que se presionó el botón ordena la concurrencia en el
          // servidor: dos cajas pidiendo a la vez reciben números distintos.
          requestedAt: new Date().toISOString(),
          clientRequestId: newId(),
        },
      });
      setAviso(
        "Generando el archivo. Puede seguir trabajando: continúa en segundo plano.",
      );
      await cargar();
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError
          ? problema.message
          : "No fue posible solicitar la descarga.",
      );
    } finally {
      setSolicitando(false);
    }
  };

  const descargar = async (trabajo: DownloadJobDto): Promise<void> => {
    setError(null);
    try {
      const archivo = await apiDownload(`/v1/downloads/${trabajo.id}/file`);
      const url = URL.createObjectURL(new Blob([archivo.bytes as BlobPart]));
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = archivo.fileName;
      enlace.click();
      URL.revokeObjectURL(url);
      // El temporal se elimina al COMPLETARSE la descarga, no al iniciarla.
      await cargar();
    } catch {
      setError("El archivo ya no está disponible. Vuelva a solicitarlo.");
      await cargar();
    }
  };

  return (
    <div className="tarjeta">
      <h2>Descarga de expediente</h2>

      {aviso !== null && (
        <div className="aviso-caja ok" role="status">
          {aviso}
        </div>
      )}
      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}
      {fallido !== undefined && aviso === null && (
        <div className="aviso-caja error" role="alert">
          La última descarga no se completó. Puede volver a solicitarla.
        </div>
      )}

      <div className="acciones">
        <button
          type="button"
          className="boton"
          onClick={() => void solicitar()}
          disabled={solicitando || activo}
        >
          {activo ? "Generando…" : "Descargar"}
        </button>

        {listo !== undefined && (
          <button
            type="button"
            className="boton primario"
            onClick={() => void descargar(listo)}
          >
            Obtener {listo.fileName}
          </button>
        )}
      </div>

      {activo && (
        <p className="ayuda">
          Puede salir de esta pantalla: la generación continúa y el archivo
          aparecerá en Notificaciones cuando esté listo.
        </p>
      )}
    </div>
  );
}
